import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { ECourierVehicleType } from "../../Enum/ECourierVehicleType"
import { useCreateCourier } from "../../hooks/Courier/useCreateCourier"
import { useSaveCourier } from "../../hooks/Courier/useSaveCourier"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { CourierAuthState, useCourierViewContext } from "../../context/useCourierViewContext"
import { API_ROUTES } from "../../configuration/RoutesApi"
import useLogin from "../../hooks/Components/useLogin"
import { ECourierView } from "../../Enum/ECourierView"
import { ECourierSignType } from "../../Enum/ECourierSignType"
import { useConfirmation } from "../../context/useConfirmationContext"
import { useMessage } from "../../context/useMessageContext"
import { useResetPassword } from "../../hooks/Components/useResetPassword"
import { useTranslation } from "react-i18next"
import { useDownloadContract } from "../../hooks/Contract/useDownloadContract"
import { EContractProfile } from "../../Enum/EContractProfile"
import InputField from "../../components/InputField/InputField"

const vehicleIcons: Record<number, string> = {
  [ECourierVehicleType.Bike]: "🚲",
  [ECourierVehicleType.MotorCycle]: "🏍️",
  [ECourierVehicleType.Car]: "🚗",
  [ECourierVehicleType.Others]: "🚶",
}

const vehicleNames: Record<number, string> = {
  [ECourierVehicleType.Bike]: "Bicicleta",
  [ECourierVehicleType.MotorCycle]: "Moto",
  [ECourierVehicleType.Car]: "Carro",
  [ECourierVehicleType.Others]: "Outros",
}

export function CourierRegisterPage() {
  const { t } = useTranslation("frontend")

  const [searchParams] = useSearchParams()

  const [authMode, setAuthMode] = useState<ECourierSignType>(ECourierSignType.SignUp)

  const isSignUp = authMode === ECourierSignType.SignUp

  const isSignIn = authMode === ECourierSignType.SignIn

  const { login, setCurrentView } = useCourierViewContext()

  const { setNewConfirmation } = useConfirmation()

  const { setMessage } = useMessage()

  const { HandleRecoverPassword } = useResetPassword()

  const { handleDownloadContractAsync } = useDownloadContract()

  const {
    HandleCourierLogin,
    setEmail: setEmailLogin,
    setPassword: setPasswordLogin,
    email: emailLogin,
    password: passwordLogin,
  } = useLogin()

  const { handleTryCourierCreateAccountAsync } = useSaveCourier()

  const {
    name,
    phone,
    email,
    password,
    vehicleType,
    isValid,
    loading,
    setName,
    setPhone,
    setEmail,
    setPassword,
    setLoading,
    setVehicleType,
    handleSubmitForm,
    resetForm,
    acceptedFinancialTerm,
    setAcceptedFinancialTerm,
  } = useCreateCourier()

  const HandleSignInSubmit = async (e?: React.FormEvent, emailLogin1?: string, passwordLogin1?: string) => {
    e?.preventDefault()

    await HandleCourierLogin(
      API_ROUTES.DELIVERY.COURIER_LOGIN,
      emailLogin1 ?? emailLogin,
      passwordLogin1 ?? passwordLogin,
      (authState: CourierAuthState) => {
        if (authState.status) {
          setCurrentView(ECourierView.SignInView)

          login(authState.token)
        }
      },
    )
  }

  const handleSignUpSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()

    try {
      setLoading(true)
      const dto = handleSubmitForm()

      await handleTryCourierCreateAccountAsync(dto, () => {
        HandleSignInSubmit(undefined, dto.email, dto.password)

        resetForm()
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const mode = searchParams.get("mode")

    if (mode === "signin") {
      setAuthMode(ECourierSignType.SignIn)
      return
    }

    if (mode === "signup") {
      setAuthMode(ECourierSignType.SignUp)
    }
  }, [searchParams])

  return (
    <Animate>
      <main className="flex-1 pt-20">
        <section className="relative overflow-hidden flex items-center py-12 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 lg:col-span-6 space-y-8 lg:pr-8">
              <div className="space-y-4">
                <Animate type={AnimationTypes.slideRightIn} duration="1.2s">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white text-orange-700 text-xs font-bold uppercase tracking-wider border-2 border-orange-200">
                    <span className="w-2 h-2 rounded-xl bg-orange-600 animate-pulse" />
                    Fature Mais
                  </span>
                </Animate>

                <Animate type={AnimationTypes.fadeIn} duration="1.5s">
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight">
                    {t("courier_sign.be_your_own")}{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-500">
                      chefe
                    </span>{" "}
                    hoje mesmo.
                  </h1>
                </Animate>

                <Animate type={AnimationTypes.fadeIn} duration="2s">
                  <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                    {t("courier_sign.hero_desc")}
                    performance.
                  </p>
                </Animate>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Animate type={AnimationTypes.fadeIn} duration="2.2s">
                  <div className="p-5 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition group">
                    <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">💰</div>

                    <h3 className="font-bold text-slate-900">{t("courier_sign.daily_earnings")}</h3>

                    <p className="text-sm text-slate-500">{t("courier_sign.receive_same_day")}</p>
                  </div>
                </Animate>

                <Animate type={AnimationTypes.fadeIn} duration="3s">
                  <div className="p-5 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition group">
                    <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">⚡</div>

                    <h3 className="font-bold text-slate-900">{t("courier_sign.fast_approval")}</h3>

                    <p className="text-sm text-slate-500">Comece a entregar em menos de 24 horas.</p>
                  </div>
                </Animate>
              </div>
            </div>

            <Animate type={AnimationTypes.slideRightIn} duration="1.5s">
              <div className="order-1 lg:order-2 lg:col-span-6 flex justify-center lg:justify-end">
                <div className="w-full max-w-[480px] bg-white rounded-xl shadow-[0_20px_50px_rgba(79,70,229,0.15)] p-8 sm:p-10 border border-slate-100 relative">
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-yellow-400 rounded-xl flex items-center justify-center text-3xl shadow-lg border-4 border-white">
                    🔥
                  </div>

                  <div className="mb-8 text-center">
                    <h2 className="text-2xl font-black text-slate-900 mb-2">
                      {isSignUp ? "Criar sua conta" : "Entrar na sua conta"}
                    </h2>

                    <p className="text-slate-500 text-sm">
                      {isSignUp ? t("courier_sign.fill_data") : t("courier_sign.enter_data")}
                    </p>
                  </div>

                  <form onSubmit={isSignUp ? handleSignUpSubmit : HandleSignInSubmit} className="space-y-5">
                    {isSignUp && (
                      <>
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-400 uppercase ml-1">
                            {t("courier_sign.personal_info")}
                          </label>

                          <InputField
                            required
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Nome completo"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.nomeCompleto"
                          />

                          <InputField
                            required
                            type="text"
                            maxLength={11}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
                            placeholder="Telefone (WhatsApp)"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.telefoneWhatsApp"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-400 uppercase ml-1">Acesso</label>

                          <InputField
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="E-mail"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.eMail"
                          />

                          <InputField
                            required
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Senha de acesso"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.senhaDeAcesso"
                          />
                        </div>

                        <div className="space-y-3">
                          <label className="text-xs font-bold text-slate-400 uppercase ml-1">
                            {t("courier_sign.delivery_vehicle")}
                          </label>

                          <div className="grid grid-cols-4 gap-2">
                            {[
                              ECourierVehicleType.Bike,
                              ECourierVehicleType.MotorCycle,
                              ECourierVehicleType.Car,
                              ECourierVehicleType.Others,
                            ].map((e) => (
                              <button
                                key={e}
                                type="button"
                                onClick={() => setVehicleType(e)}
                                className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-all ${
                                  vehicleType === e
                                    ? "bg-orange-600 border-orange-600 text-white shadow-lg shadow-orange-200"
                                    : "bg-slate-50 border-slate-200 text-slate-400 hover:border-orange-300"
                                }`}
                                data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.button.setVehicleTypeE"
                              >
                                <span className="text-xl mb-1">{vehicleIcons[e]}</span>

                                <span className="text-[10px] font-bold uppercase">{vehicleNames[e]}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-slate-700">
                          <p className="font-bold text-red-700">Aviso obrigatório sobre pagamentos</p>

                          <p className="mt-1">
                            O app apenas registra e organiza informações de controle. Quem paga o entregador é
                            exclusivamente o restaurante responsável pela entrega.
                          </p>

                          <label className="mt-3 flex items-start gap-2 font-medium text-slate-800">
                            <input
                              type="checkbox"
                              checked={acceptedFinancialTerm}
                              onChange={(e) => setAcceptedFinancialTerm(e.target.checked)}
                              className="mt-1 h-4 w-4 accent-red-600"
                              data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.input.input"
                            />

                            <span>
                              Li e concordo que não há custódia, intermediação financeira ou pagamento do app ao
                              entregador.
                            </span>
                          </label>
                        </div>
                      </>
                    )}

                    {isSignIn && (
                      <Animate type={AnimationTypes.fadeIn}>
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-400 uppercase ml-1">Acesso</label>

                          <InputField
                            required
                            type="email"
                            value={emailLogin}
                            onChange={(e) => setEmailLogin(e.target.value)}
                            placeholder="E-mail"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.eMail2"
                          />

                          <InputField
                            required
                            type="password"
                            value={passwordLogin}
                            onChange={(e) => setPasswordLogin(e.target.value)}
                            placeholder="Senha"
                            descriptionKey="interactionDescriptions:pages.delivery.courierSignPage.input.senha"
                          />
                        </div>
                      </Animate>
                    )}

                    {isSignUp && (
                      <Animate type={AnimationTypes.fadeIn}>
                        <div className="rounded-lg border-2 border-orange-100 px-4 py-3 text-xs text-slate-600">
                          <span className="font-semibold text-green-700">✓</span>{" "}
                          {t("courier_sign.contract_auto_accept")}{" "}
                          <button
                            type="button"
                            onClick={() => handleDownloadContractAsync(EContractProfile.Courier)}
                            className="font-bold text-orange-600 hover:underline"
                            data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.button.handleDownloadContractAsyncEContractProfileCourier"
                          >
                            {t("courier_sign.contract_download_link")}
                          </button>
                        </div>
                      </Animate>
                    )}

                    {isSignIn && emailLogin.length > 0 && (
                      <Animate type={AnimationTypes.fadeIn}>
                        <div className="text-right">
                          <button
                            type="button"
                            className="text-sm text-orange-600 font-bold hover:underline"
                            onClick={() =>
                              setNewConfirmation(
                                "Recuperar conta",
                                "Deseja receber um link para redefinir sua senha?",
                                async () => {
                                  await HandleRecoverPassword(
                                    emailLogin,
                                    () =>
                                      setMessage(
                                        t("courier_sign.recover_password_title"),
                                        t("courier_sign.recover_password_description"),
                                      ),
                                    API_ROUTES.DELIVERY.SEND_COURIER_RECOVER_LINK,
                                  )
                                },
                              )
                            }
                            data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.button.recoverPasswordTitle"
                          >
                            Esqueci minha senha
                          </button>
                        </div>
                      </Animate>
                    )}

                    <button
                      disabled={isSignUp ? !isValid || loading : !emailLogin || !passwordLogin}
                      className={`w-full py-5 rounded-2xl font-black text-lg transition-all shadow-xl flex items-center justify-center gap-3 active:scale-95 ${
                        isSignUp
                          ? !isValid || loading
                            ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                            : "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200"
                          : !emailLogin || !passwordLogin
                            ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                            : "bg-orange-600 text-white hover:bg-orange-700 shadow-orange-200"
                      }`}
                      data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.button.startNow"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                          Processando...
                        </div>
                      ) : isSignUp ? (
                        t("courier_sign.start_now")
                      ) : (
                        "Entrar"
                      )}
                    </button>

                    <div className="text-center pt-2">
                      <p className="text-sm text-slate-500">
                        {isSignUp ? t("courier_sign.already_team") : t("courier_sign.no_account")}{" "}
                        <button
                          type="button"
                          onClick={() => setAuthMode(isSignUp ? ECourierSignType.SignIn : ECourierSignType.SignUp)}
                          className="text-orange-600 font-bold hover:underline"
                          data-interaction-description-key="interactionDescriptions:pages.delivery.courierSignPage.button.setAuthModeIsSignUpECourierSignTypeSignInECourierSignTypeSig"
                        >
                          {isSignUp ? "Entrar agora" : "Criar conta"}
                        </button>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </Animate>
          </div>
        </section>
      </main>
    </Animate>
  )
}
